/**
 * One engine setting in the Admin guide: its label (the mechanics behind
 * ⓘ), the control — choice buttons when only some values are allowed, a
 * number box otherwise — its unit, and "default N" once it differs. The box
 * does not clamp: a value out of range is shown and refused by the section.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenEngineSettingDef } from '@trafficpulse/shared';

import { HintDot, NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import { ChoiceChips } from '../../editor/general/controls/ChoiceChips';
import { MINIMAL_FIELD } from '../../editor/general/controls/field-style';
import { unitWords } from './section-draft';

interface Props {
  def: DropGenEngineSettingDef;
  value: number;
  onChange: (value: number) => void;
}

const Control: FC<Props> = ({ def, value, onChange }) =>
  def.choices ? (
    <div className="flex items-center [&_[role=radio]]:h-7 [&_[role=radio]]:px-2.5 [&_[role=radio]]:text-xs">
      <ChoiceChips options={def.choices.map((c) => ({ value: String(c), label: `${c} ${unitWords(def)}` }))} value={String(value)} onChange={(v) => onChange(Number(v))} ariaLabel={def.label} />
    </div>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <NumberField value={value} onChange={onChange} min={0} aria-label={def.label} className={cn(MINIMAL_FIELD, 'w-24 px-2 text-right')} />
      <span className="text-xs text-muted-foreground">{unitWords(def)}</span>
    </span>
  );

export const SettingField: FC<Props> = (props) => {
  const { def, value } = props;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border/40 py-2 first:border-t-0" data-testid={`engine-setting-${def.key}`}>
      <span className="flex min-w-[15rem] items-center gap-1.5 text-[13px] font-medium">
        {def.label}
        <HintDot label={def.hint} />
      </span>
      <Control {...props} />
      {value !== def.default ? <span className="text-[11px] text-muted-foreground">default {def.default.toLocaleString('en-US')}</span> : null}
    </div>
  );
};
